import React from 'react';

import {assert} from 'Asserts';
import {ProcessAsyncRecordStatus, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {sidepanelProcessItemIsProcessTask} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import FixFailedRoutingAndMappingModal from 'processes/modals/fix-failed-routing-and-mapping/FixFailedRoutingAndMappingModal';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

type SidepanelProcessTask = ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>;

type SidepanelProcessOutput = ExtractByTypename<
  ItemListingQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

interface FixFailedMappingButtonProps {
  selectedItems: Array<SidepanelProcessTask | SidepanelProcessOutput>;
  dataTestAttrs?: DataTestAttrs;
}

function getTooltipErrorMessage(
  selectedItems: Array<SidepanelProcessTask | SidepanelProcessOutput>
): string | null {
  if (selectedItems.length === 0) {
    return 'No items selected';
  } else if (selectedItems.length > 1) {
    return 'Only one item can be selected at a time';
  }

  const selectedItem = selectedItems[0];

  const taskForSelectedItem = sidepanelProcessItemIsProcessTask(selectedItem)
    ? selectedItem
    : selectedItem.task;

  if (taskForSelectedItem.schema.clientOrigin !== ProcessClientOrigin.PROCESSES) {
    return 'Only supported for workflows tasks';
  }

  return taskForSelectedItem.processAsyncRecords.every(
    (processAsyncRecord) => processAsyncRecord.status === ProcessAsyncRecordStatus.ACTIVE
  )
    ? 'Item must have at least one failed mapping'
    : null;
}

export function FixFailedMappingButton(props: FixFailedMappingButtonProps): JSX.Element {
  const tooltipMessage = getTooltipErrorMessage(props.selectedItems);

  return (
    <TooltipWrapper tooltipTitle={tooltipMessage ?? 'Fix failed mapping'}>
      <Button
        disabled={tooltipMessage != null}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        onClick={async () => {
          assert(props.selectedItems.length === 1, 'Expected exactly one item to be selected', {
            selectedItems: props.selectedItems,
          });
          const selectedItem = props.selectedItems[0];
          await runModal(FixFailedRoutingAndMappingModal, {
            selectedId: sidepanelProcessItemIsProcessTask(selectedItem)
              ? selectedItem.id
              : selectedItem.task.id,
          });
        }}
        icon="exclamation-circle"
        dataTestAttrs={props.dataTestAttrs}
      />
    </TooltipWrapper>
  );
}
